/**
 * Boerenbridge — rondeplanning en puntentelling.
 *
 * Regels: https://cardgameshub.com/nl/kenniscentrum/boerenbridge-regels/
 *
 * Rondes
 *   Er wordt met één deck van 52 kaarten gespeeld. Het maximale aantal kaarten
 *   per speler is dus floor(52 / aantal spelers). De rondes lopen op van 1 kaart
 *   tot dat maximum en daarna weer terug tot 1 kaart.
 *
 * Punten
 *   - Bod exact gehaald : 10 + 3 punten per gehaalde slag (een gehaald bod van 0 = 10)
 *   - Bod niet gehaald  : −3 punten per slag verschil
 *
 * Bieden
 *   De som van alle biedingen mag niet gelijk zijn aan het aantal kaarten van de
 *   ronde. De deler biedt als laatste en kan dat ene getal dus niet kiezen.
 */

import { SUITS } from './suits'

export const DECK_SIZE = 52
export const MIN_PLAYERS = 2
export const MAX_PLAYERS = 8
export const DEFAULT_PLAYERS = 4

export const CORRECT_BID_BONUS = 10
export const POINTS_PER_TRICK = 3

/** Elke ronde heeft een troefkleur; te kiezen op het biedscherm. */
export const TRUMP_OPTIONS = [...SUITS]

export function trumpById(id) {
  return TRUMP_OPTIONS.find(t => t.id === id) || null
}

/** Maximaal aantal kaarten per speler dat het deck toelaat. */
export function maxCardsPerRound(playerCount) {
  return Math.floor(DECK_SIZE / playerCount)
}

/** Houdt de hoogste ronde tussen 1 kaart en wat het deck toelaat. */
export function clampPeakCards(playerCount, peakCards) {
  const limit = maxCardsPerRound(playerCount)
  const wanted = Math.round(Number(peakCards))
  if (!Number.isFinite(wanted)) return limit
  return Math.min(Math.max(wanted, 1), limit)
}

/**
 * Aantal kaarten per ronde: 1 → hoogste ronde → 1.
 * Zonder opgegeven piek wordt het maximum van het deck gebruikt.
 */
export function buildRoundPlan(playerCount, peakCards) {
  const peak = clampPeakCards(playerCount, peakCards ?? maxCardsPerRound(playerCount))
  const plan = []
  for (let cards = 1; cards <= peak; cards++) plan.push(cards)
  for (let cards = peak - 1; cards >= 1; cards--) plan.push(cards)
  return plan
}

/** Punten voor één speler in één ronde. */
export function roundPoints(bid, tricksTaken) {
  if (bid === tricksTaken) return CORRECT_BID_BONUS + POINTS_PER_TRICK * tricksTaken
  return -POINTS_PER_TRICK * Math.abs(bid - tricksTaken)
}

/**
 * Het bod dat de laatste bieder niet mag kiezen: het getal dat de som van alle
 * biedingen precies gelijk zou maken aan het aantal kaarten. Geeft null wanneer
 * dat getal buiten het toegestane bereik valt (dan is elk bod toegestaan).
 */
export function forbiddenBid(cards, bidsSoFar) {
  const total = bidsSoFar.reduce((sum, bid) => sum + (bid || 0), 0)
  const remaining = cards - total
  return remaining >= 0 && remaining <= cards ? remaining : null
}
